import { useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Calendar, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Header } from '@/components/header'
import { Logo } from '@/components/logo'
import { roadmapConfig, getStatusConfig, getCategoryConfig } from '@/config/roadmap'

const RoadmapPage = () => {
  const navigate = useNavigate()

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden border-b">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5" />
        <div className="container mx-auto max-w-6xl px-6 py-16 md:py-24 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center space-y-6"
          >
            <Badge variant="outline" className="px-4 py-1.5">
              <Sparkles className="w-3.5 h-3.5 mr-2" />
              Product Roadmap
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Building the Future of
              <span className="text-primary"> LCA Analysis</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See what we're working on and what's coming next. Our roadmap is shaped by user
              feedback and our mission to make LCA accessible through AI.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Roadmap Sections */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto max-w-6xl px-6">
          <div className="space-y-16">
            {roadmapConfig.map((section, sectionIndex) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: sectionIndex * 0.1 }}
                className="space-y-6"
              >
                <div className="space-y-2">
                  <h2 className="text-2xl md:text-3xl font-bold">{section.title}</h2>
                  <p className="text-muted-foreground">{section.description}</p>
                </div>

                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {section.items.map((item, itemIndex) => {
                    const statusConfig = getStatusConfig(item.status)
                    const categoryConfig = getCategoryConfig(item.category)

                    return (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: sectionIndex * 0.1 + itemIndex * 0.05 }}
                      >
                        <Card className="h-full hover:shadow-md transition-shadow">
                          <CardHeader className="pb-3">
                            <div className="flex items-start justify-between gap-2">
                              <CardTitle className="text-lg leading-tight">{item.title}</CardTitle>
                              <Badge
                                variant="outline"
                                className={`shrink-0 text-xs ${statusConfig.color}`}
                              >
                                {statusConfig.icon} {statusConfig.label}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 pt-1">
                              <Badge
                                variant="secondary"
                                className={`text-xs ${categoryConfig.color}`}
                              >
                                {categoryConfig.label}
                              </Badge>
                              {item.quarter && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {item.quarter}
                                </span>
                              )}
                            </div>
                          </CardHeader>
                          <CardContent>
                            <CardDescription className="text-sm">
                              {item.description}
                            </CardDescription>
                          </CardContent>
                        </Card>
                      </motion.div>
                    )
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 border-t bg-muted/30">
        <div className="container mx-auto max-w-4xl px-6 text-center space-y-6">
          <h2 className="text-2xl md:text-3xl font-bold">Have a Feature Request?</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            We'd love to hear from you! Your feedback helps shape our product roadmap and ensures
            we're building features that matter most to LCA professionals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => navigate('/auth?mode=signup')}>
              Get Started Free
            </Button>
            <Button size="lg" variant="outline" onClick={() => (window.location.href = '/')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-8">
        <div className="container mx-auto max-w-6xl px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Logo size={24} showText={true} />
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} EcoSemantic. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default RoadmapPage
